import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, PhoneCall } from "lucide-react";

const fmt = (n) => `$${Math.round(n).toLocaleString("en-US")}`;

export default function ROICalculator({ onBuildQuote }) {
  const [machines, setMachines] = useState(3);
  const [hours, setHours] = useState(40);
  const [rate, setRate] = useState(120);
  const [saving, setSaving] = useState(15);

  const annual = machines * hours * 48 * rate * (saving / 100);

  const sliders = [
    { label: "Excavators", value: machines, set: setMachines, min: 1, max: 20, fmtV: (v) => `${v}`, testid: "roi-machines-slider" },
    { label: "Operating hours / week / machine", value: hours, set: setHours, min: 10, max: 80, fmtV: (v) => `${v} h`, testid: "roi-hours-slider" },
    { label: "Fully loaded machine rate", value: rate, set: setRate, min: 50, max: 300, fmtV: (v) => `$${v}/h`, testid: "roi-rate-slider" },
    { label: "Efficiency gain with machine control", value: saving, set: setSaving, min: 5, max: 30, fmtV: (v) => `${v}%`, testid: "roi-saving-slider" },
  ];

  return (
    <div data-testid="roi-calculator" className="grid border border-line bg-surface lg:grid-cols-[1fr_440px]">
      <div className="p-8 sm:p-12 lg:p-14">
        <div className="grid gap-10 sm:grid-cols-2">
          {sliders.map((s) => (
            <div key={s.label}>
              <div className="flex items-center justify-between">
                <label className="font-mono text-xs uppercase tracking-[0.25em] text-fog">{s.label}</label>
                <span className="font-mono text-base text-neon">{s.fmtV(s.value)}</span>
              </div>
              <input
                type="range"
                min={s.min}
                max={s.max}
                value={s.value}
                onChange={(e) => s.set(Number(e.target.value))}
                data-testid={s.testid}
                aria-label={s.label}
                className="mt-4 w-full accent-[#00FF66]"
              />
            </div>
          ))}
        </div>
        <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.2em] text-fog/60">
          Illustrative estimate — tune with your own numbers. Actual savings depend on project mix, soil and crew.
        </p>
      </div>

      <div className="flex flex-col justify-center border-t border-line bg-deep p-8 sm:p-12 lg:border-l lg:border-t-0">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-signal">Projected annual saving</p>
        <p className="mt-4 font-display text-6xl font-black text-neon sm:text-7xl" data-testid="roi-annual-saving">
          {fmt(annual)}
        </p>
        <p className="mt-4 text-sm leading-relaxed text-fog">
          Across {machines} machine{machines > 1 ? "s" : ""} at {hours} h/week with a {saving}% efficiency gain.
        </p>

        <div className="mt-10 space-y-3">
          <button
            onClick={onBuildQuote}
            data-testid="roi-build-quote-btn"
            className="group inline-flex w-full items-center justify-center gap-3 bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-cream transition-colors hover:bg-signal hover:text-deep"
          >
            Build your quote
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
          <Link
            to="/contact"
            data-testid="roi-specialist-btn"
            className="inline-flex w-full items-center justify-center gap-3 border border-line px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-white transition-colors hover:border-signal hover:text-neon"
          >
            <PhoneCall className="h-5 w-5" />
            Talk to a specialist
          </Link>
        </div>
      </div>
    </div>
  );
}
